import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

const CONTACT_FUNCTION_URL = 'https://us-central1-sagient-498216.cloudfunctions.net/contact';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', company: '', size: '', service: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(CONTACT_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus('sent');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 16px', borderRadius: 8,
    border: '1.5px solid #B5D4F4', fontSize: '0.95rem', fontFamily: 'inherit',
    color: '#0A1628', outline: 'none', transition: '0.15s',
    background: '#fff',
  };

  const labelStyle = { display: 'block', fontWeight: 500, marginBottom: 6, fontSize: '0.9rem', color: '#0A1628' };

  return (
    <>
      <Helmet>
        <title>Book a Free Discovery Call | Sagient Partners</title>
        <meta name="description" content="Schedule a free 30-minute discovery call with Sagient Partners. We'll listen to your business challenges and give you a straight answer about where AI can help." />
        <link rel="canonical" href="https://sagientpartners.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sagientpartners.com/contact" />
        <meta property="og:title" content="Book a Free Discovery Call | Sagient Partners" />
        <meta property="og:description" content="Schedule a free 30-minute discovery call with Sagient Partners. We'll listen to your business challenges and give you a straight answer about where AI can help." />
        <meta property="og:image" content="https://sagientpartners.com/social_card.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Book a Free Discovery Call | Sagient Partners" />
        <meta name="twitter:description" content="Schedule a free 30-minute discovery call with Sagient Partners. We'll listen to your business challenges and give you a straight answer about where AI can help." />
        <meta name="twitter:image" content="https://sagientpartners.com/social_card.png" />
      </Helmet>
      {/* Header */}
      <section style={{ background: 'linear-gradient(135deg, #0C447C 0%, #0A1628 100%)', padding: '40px 0 48px', color: '#fff' }}>
        <div className="container">
          <p className="section-label" style={{ color: '#B5D4F4' }}>Get Started</p>
          <h1 style={{ color: '#fff', maxWidth: 560, marginBottom: 20 }}>Let's Talk About Your Business</h1>
          <p style={{ color: '#B5D4F4', fontSize: '1.1rem', maxWidth: 520 }}>
            Start with a free 30-minute discovery call. We'll listen, ask the right questions, and give you a straight answer about what AI can do for your business.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container" style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 64, alignItems: 'start' }}>
          {/* Left: info */}
          <div>
            <h2 style={{ marginBottom: 24 }}>Contact Information</h2>
            {[
              { icon: '✉️', label: 'Email', value: 'info@sagientpartners.com', href: 'mailto:info@sagientpartners.com' },
              { icon: '🌐', label: 'Website', value: 'sagientpartners.com', href: 'https://sagientpartners.com' },
              { icon: '📍', label: 'Location', value: 'San Francisco Bay Area', href: null },
            ].map(c => (
              <div key={c.label} style={{ display: 'flex', gap: 16, marginBottom: 24, alignItems: 'flex-start' }}>
                <div style={{ fontSize: '1.4rem', width: 32, flexShrink: 0 }}>{c.icon}</div>
                <div>
                  <p style={{ fontWeight: 600, fontSize: '0.88rem', color: '#64748B', marginBottom: 2 }}>{c.label}</p>
                  {c.href
                    ? <a href={c.href} style={{ color: '#378ADD', fontWeight: 500 }}>{c.value}</a>
                    : <p style={{ fontWeight: 500 }}>{c.value}</p>
                  }
                </div>
              </div>
            ))}

            <div className="divider" />

            <h3 style={{ marginBottom: 16 }}>What to Expect</h3>
            {[
              'A 30-minute no-pressure conversation',
              'Questions about your operations and goals',
              'A candid answer about where AI fits',
              'A recommended next step - no hard sell',
            ].map(item => (
              <div key={item} style={{ display: 'flex', gap: 10, marginBottom: 12, fontSize: '0.9rem', color: '#64748B' }}>
                <span style={{ color: '#378ADD', fontWeight: 700 }}>✓</span>
                {item}
              </div>
            ))}
          </div>

          {/* Right: form */}
          <div className="card">
            {status === 'sent' ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: '3rem', marginBottom: 20 }}>✅</div>
                <h2 style={{ marginBottom: 12 }}>Message Sent!</h2>
                <p>Thanks for reaching out. We'll be in touch within one business day.</p>
              </div>
            ) : status === 'error' ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: '3rem', marginBottom: 20 }}>⚠️</div>
                <h2 style={{ marginBottom: 12 }}>Something went wrong</h2>
                <p>Please try again or email us directly at <a href="mailto:info@sagientpartners.com" style={{ color: '#378ADD' }}>info@sagientpartners.com</a>.</p>
                <button onClick={() => setStatus('idle')} className="btn btn-primary" style={{ marginTop: 20 }}>Try Again</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h3 style={{ marginBottom: 24 }}>Book a Discovery Call</h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                  <div>
                    <label style={labelStyle}>Your Name *</label>
                    <input name="name" required value={form.name} onChange={handleChange} placeholder="Jane Smith" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Email Address *</label>
                    <input name="email" type="email" required value={form.email} onChange={handleChange} placeholder="jane@company.com" style={inputStyle} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                  <div>
                    <label style={labelStyle}>Company Name</label>
                    <input name="company" value={form.company} onChange={handleChange} placeholder="Acme Co." style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Company Size</label>
                    <select name="size" value={form.size} onChange={handleChange} style={{ ...inputStyle, cursor: 'pointer' }}>
                      <option value="">Select…</option>
                      <option>1–10 employees</option>
                      <option>11–50 employees</option>
                      <option>51–200 employees</option>
                      <option>201–500 employees</option>
                      <option>500+ employees</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>Service Interest</label>
                  <select name="service" value={form.service} onChange={handleChange} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="">Not sure yet</option>
                    <option>AI Readiness Assessment</option>
                    <option>AI Strategy Implementation</option>
                    <option>AI Advisor</option>
                    <option>Just exploring</option>
                  </select>
                </div>

                <div style={{ marginBottom: 28 }}>
                  <label style={labelStyle}>Tell us about your business *</label>
                  <textarea
                    name="message" required value={form.message} onChange={handleChange}
                    placeholder="What does your business do? What are the manual or time-consuming processes you'd most like to fix?"
                    rows={5}
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="btn btn-primary"
                  style={{ width: '100%', justifyContent: 'center', fontSize: '1rem', padding: '14px', opacity: status === 'sending' ? 0.7 : 1 }}
                >
                  {status === 'sending' ? 'Sending…' : 'Send Discovery Call Request →'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
